import request from "@/api/index";
import cacheRequest from "@/utils/cache";

/**
 * @description 深拷贝，支持函数、undefined、symbol等
 * @param {Object} target
 * @returns {Object}
 */
export function deepClone(obj, visited = new WeakMap()) {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof RegExp) {
    return new RegExp(obj);
  }

  if (obj instanceof Function) {
    return function () {
      return obj.apply(this, arguments);
    };
  }

  if (typeof obj === "symbol") {
    return obj;
  }

  // Check for cyclic references
  if (visited.has(obj)) {
    return visited.get(obj);
  }

  const newObj = Array.isArray(obj) ? [] : {};

  // Add this object to the visited set to prevent cyclic references
  visited.set(obj, newObj);

  for (let key of Reflect.ownKeys(obj)) {
    if (key === "__ob__") continue; // 忽略vue特殊属性
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key] = deepClone(obj[key], visited);
    }
  }

  return newObj;
}

/**
 * @description 将对象中符合函数写法的字符串转化为函数，改变原对象
 * @param {Object} object
 */
export function digStrFn(object) {
  for (let key in object) {
    const value = object[key],
      type = typeof value;
    if (value && type === "object" && !Array.isArray(value)) {
      // 对象递归
      digStrFn(value);
    } else {
      const fn = strToFn(value);
      if (fn) object[key] = fn;
    }
  }
}

/**
 * @description 将符合函数写法的字符串转化为函数
 * @param {String} strFn
 * @returns {(Function|null)}
 */
export function strToFn(strFn) {
  if (
    typeof strFn === "string" &&
    (/^\s*(async *)?(\(.*\) *=>.+)\s*$/s.test(strFn) || /^\s*((async +)?function *\(.*\)\s*\{.*\})\s*$/s.test(strFn))
  ) {
    // 特殊字符串尝试转化为函数
    try {
      const fn = new Function(`return ${strFn}`)();
      console.log({fn});
      return fn;
    } catch(err) {
      console.log({err});
      return null;
    }
  } else return null;
}

/**
 * @description 金额格式化
 * @param {Number} amount 待格式化金额
 * @param {String} prefix 前缀
 * @param {String} suffix 后缀
 * @param {Number} decimals 保留小数位数
 * @param {String} thousandSymbol 千分符
 * @return {String}
 */
export function amountFormatter({ amount = 0, prefix = "", suffix = "", decimals = 0, thousandSymbol = "" }) {
  amount = Number(amount);
  if (isNaN(amount)) amount = 0;
  return prefix + amount.toFixed(decimals).replace(/(\d)(?=(\d{3})+(\.|$))/g, `$1${thousandSymbol}`) + suffix;
}

/**
 * @description 快捷发起请求（报表用）
 * @param {Boolean} cache 是否缓存
 * @param {String} url 请求地址
 * @param {String} method 请求方式
 * @returns {Promise} 请求Promise
 */
export async function quickRequest({ cache = false, url = "", method = "get" }) {
  const requestOption = { url, method };
  return await (cache ? cacheRequest : request)(requestOption);
}
